import React from "react";
import { Link } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";

const RealEstateAssistPrivacy = () => {
  return (
    <Container fluid style={styles.container}>
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8} style={styles.content}>
            <h1 style={styles.title}>
              Privacy Policy — Real Estate Agent Assist
            </h1>
            <p style={styles.effectiveDate}>
              Effective date: September 30, 2026
            </p>
            <p style={styles.paragraph}>
              Real Estate Agent Assist (&quot;RE Assist&quot;, &quot;the
              app&quot;) is published by ACJ Operations LLC.
            </p>

            <h2 style={styles.heading}>The short version</h2>
            <p style={styles.paragraph}>
              Everything you put into Real Estate Agent Assist stays on your
              device or in your own cloud account: iCloud on iPhone and iPad,
              or on Android a folder you choose, such as one in your Google
              Drive. We do not collect, receive, sell, or share any of your
              data — we have no servers to send it to. There are no accounts,
              no analytics, and no tracking. A few features use Apple services
              on your behalf, described below; the Android app&apos;s
              equivalents are described under <strong style={styles.strong}>On
              Android</strong>.
            </p>

            <h2 style={styles.heading}>What data the app stores</h2>
            <p style={styles.paragraph}>
              The app stores the information you enter — listings, addresses,
              prices, commission figures, key dates, checklists, contacts,
              notes, and photos you attach — in a local database inside the
              app&apos;s private storage on your device. This data:
            </p>
            <ul style={styles.list}>
              <li>never leaves your device through the app;</li>
              <li>
                is included in your normal device backups (iCloud or computer
                backups), which are governed by Apple&apos;s privacy practices,
                not ours;
              </li>
              <li>is deleted when you delete the app.</li>
            </ul>

            <h2 style={styles.heading}>What we collect</h2>
            <p style={styles.paragraph}>
              Nothing. The app contains no analytics, advertising, or tracking
              SDKs, and never sends your data to us or to any third party other
              than Apple (or, on Android, your phone&apos;s own Google
              services), as described in the sections below.
            </p>

            <h2 style={styles.heading}>Importing a CSV</h2>
            <p style={styles.paragraph}>
              If you import listings from a CSV file, the file is read on your
              device and the listings are saved locally. The file itself is not
              copied anywhere else and nothing is uploaded.
            </p>

            <h2 style={styles.heading}>Photos</h2>
            <p style={styles.paragraph}>
              When you attach photos to a listing, the app uses Apple&apos;s
              photo picker, which shares only the specific photos you choose.
              The app never has access to your photo library.
            </p>

            <h2 style={styles.heading}>Notifications</h2>
            <p style={styles.paragraph}>
              Reminders (such as closing-day alerts) are scheduled locally on
              your device. No notification data is sent to any server.
            </p>

            <h2 style={styles.heading}>
              Maps, street photos, and address suggestions
            </h2>
            <p style={styles.paragraph}>
              Some features send a property address to Apple Maps services,
              which answer from Apple&apos;s servers:
            </p>
            <ul style={styles.list}>
              <li>showing listings on the map, and getting directions;</li>
              <li>
                the street-level or aerial photo shown on a listing that has no
                photos of its own;
              </li>
              <li>address suggestions while you type an address.</li>
            </ul>
            <p style={styles.paragraph}>
              Only the address (or the text you are typing) is sent — never
              names, notes, prices, or anything else about the listing — and it
              goes to Apple, not to us. Apple&apos;s handling of it is covered
              by Apple&apos;s privacy policy. The pictures Apple returns are
              shown, not saved.
            </p>
            <p style={styles.paragraph}>
              If you open directions in Google Maps or Waze instead, that app
              receives the destination and its own privacy policy applies.
            </p>

            <h2 style={styles.heading}>Dictation</h2>
            <p style={styles.paragraph}>
              If you dictate a note (Pro), the app uses your microphone and
              Apple&apos;s speech recognition running entirely on your device.
              Audio and text are never sent anywhere, and the app only listens
              while you have dictation turned on.
            </p>

            <h2 style={styles.heading}>Documents</h2>
            <p style={styles.paragraph}>
              When you attach a document to a listing, the app saves a copy in
              the app&apos;s folder in your own iCloud Drive, under your Apple
              Account, so it survives losing your phone. The app only attaches
              documents when iCloud Drive is on. We never receive a copy.
              Removing a document from a listing does not delete the file from
              iCloud Drive; you can delete it yourself in Files.
            </p>

            <h2 style={styles.heading}>Apple Watch and widgets</h2>
            <p style={styles.paragraph}>
              The Apple Watch app and Home Screen widgets show a summary of
              your listings. The summary moves between your own devices using
              Apple&apos;s device-to-device connection and never passes through
              us.
            </p>

            <h2 style={styles.heading}>Subscriptions</h2>
            <p style={styles.paragraph}>
              Purchases are handled entirely by Apple. We never see your
              payment details; the app only learns which plan is active.
            </p>

            <h2 style={styles.heading}>Calendar</h2>
            <p style={styles.paragraph}>
              If you add a closing to your calendar (or enable Pro calendar
              sync), the app creates events using Apple&apos;s EventKit on your
              device. With the one-tap add, the app uses write-only access and
              cannot see your existing events; Pro sync uses full calendar
              access solely to keep the events it created up to date. No
              calendar data is transmitted anywhere.
            </p>

            <h2 style={styles.heading}>Backups and exports</h2>
            <p style={styles.paragraph}>
              The app can write your library to a file — a backup you make
              yourself, an automatic backup to your iCloud Drive, or a year-end
              report exported as PDF or CSV. These files are yours, and they
              are how you keep your work when the app is deleted or your phone
              is replaced.
            </p>
            <p style={styles.paragraph}>
              You should know what is in them and where they go:
            </p>
            <ul style={styles.list}>
              <li>
                <strong style={styles.strong}>They are not encrypted.</strong>{" "}
                They are plain files so you can open them, read them, hand them
                to your accountant, and move them to another app or another
                platform later. That also means anyone who can open the file
                can read it.
              </li>
              <li>
                <strong style={styles.strong}>
                  They contain client information
                </strong>{" "}
                — names, phone numbers, email addresses, property addresses,
                notes, and, unless you turn photos off, listing photos.
              </li>
              <li>
                <strong style={styles.strong}>
                  They go where you send them.
                </strong>{" "}
                A backup you share lands wherever you choose — Files, Mail,
                another app. An automatic backup goes to your own iCloud Drive,
                under your Apple Account. We never receive a copy, and we have
                no way to read one.
              </li>
              <li>
                <strong style={styles.strong}>
                  Protecting them is up to you.
                </strong>{" "}
                Once a file leaves the app it is covered by whatever service
                you put it in and by your own device security, not by this
                policy. Treat a backup the way you would treat a client file,
                because that is what it is.
              </li>
            </ul>
            <p style={styles.paragraph}>
              You can delete an automatic backup at any time from Files, and
              turn automatic backups off in Settings → Your Data.
            </p>

            <h2 style={styles.heading}>On Android</h2>
            <p style={styles.paragraph}>
              The Android app works the same way: your data is kept in the
              app&apos;s private storage on your phone, with no accounts,
              analytics, advertising, or tracking, and nothing is ever sent to
              us. Where the iPhone app uses an Apple service, the Android app
              uses your phone&apos;s own equivalent:
            </p>
            <ul style={styles.list}>
              <li>
                <strong style={styles.strong}>Backups.</strong> A backup you
                save goes wherever you choose in Android&apos;s file picker.
                Automatic backup writes to a folder you choose once — on your
                phone, on an SD card, or in a cloud folder such as your Google
                Drive — and keeps the three most recent copies there. The files
                are the same unencrypted backups described under{" "}
                <em>Backups and exports</em>, and the service that holds the
                folder (for example Google) applies its own privacy policy. We
                never receive a copy. Turn automatic backup off in Settings →
                Backup &amp; Restore.
              </li>
              <li>
                <strong style={styles.strong}>Device backup.</strong>{" "}
                Android&apos;s own backup (Google&apos;s backup of your phone)
                may include the app&apos;s data and settings, as it does for
                most apps. That backup is governed by Google&apos;s privacy
                practices, not ours.
              </li>
              <li>
                <strong style={styles.strong}>Dictation (Pro).</strong> The app
                uses your phone&apos;s speech recognition service through
                Android. Depending on your phone and its settings, that service
                — usually Google&apos;s — may process the audio on the phone or
                on its own servers; the app receives only the text, and listens
                only while dictation is on.
              </li>
              <li>
                <strong style={styles.strong}>Photos.</strong> The app uses
                Android&apos;s photo picker, which shares only the photos you
                choose. The app never has access to your photo library.
              </li>
              <li>
                <strong style={styles.strong}>Calendar.</strong> Closing events
                are written, on your phone, to the calendar you choose in
                Settings. The app reads your calendars only to list them for
                that choice and to keep the events it created up to date.
              </li>
              <li>
                <strong style={styles.strong}>Notifications</strong> are
                scheduled on your phone.
              </li>
              <li>
                <strong style={styles.strong}>Calls, texts, and email</strong>{" "}
                to a contact open your phone&apos;s own apps with the number or
                address filled in; the app sends nothing itself.
              </li>
              <li>
                <strong style={styles.strong}>Subscriptions</strong> are
                handled entirely by Google Play. We never see your payment
                details; the app only learns which plan is active.
              </li>
            </ul>
            <p style={styles.paragraph}>
              The Android app has no map, street photos, address suggestions,
              iCloud documents, Apple Watch app, or widgets, so none of the
              Apple services described above apply to it.
            </p>

            <h2 style={styles.heading}>Children</h2>
            <p style={styles.paragraph}>
              The app is not directed at children under 13 and collects no data
              from anyone.
            </p>

            <h2 style={styles.heading}>Changes</h2>
            <p style={styles.paragraph}>
              If a future version of the app adds features that change these
              practices (for example, optional cloud sync), this policy will be
              updated first, and the app will ask for your consent before any
              data leaves your device.
            </p>

            <h2 style={styles.heading}>Contact</h2>
            <p style={styles.paragraph}>
              Questions about this policy? Send us a message through the{" "}
              <Link
                to="/apps/real-estate-assist/support"
                className="footer-link"
              >
                Real Estate Agent Assist support page
              </Link>
              .
            </p>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};

export default RealEstateAssistPrivacy;

const styles = {
  container: {
    backgroundColor: "#000",
    paddingTop: 100,
    paddingBottom: 100,
  },
  content: {
    textAlign: "left",
  },
  effectiveDate: {
    color: "#ACACAC",
    fontSize: 16,
    fontStyle: "italic",
    marginBottom: 24,
  },
  heading: {
    color: "#CF3620",
    fontSize: 24,
    fontWeight: 700,
    marginTop: 32,
    marginBottom: 12,
  },
  list: {
    color: "#ACACAC",
    fontSize: 18,
    lineHeight: 1.7,
  },
  paragraph: {
    color: "#ACACAC",
    fontSize: 18,
    lineHeight: 1.7,
  },
  strong: {
    color: "#FFF",
    fontWeight: 600,
  },
  title: {
    color: "#FFF",
    fontWeight: 700,
    marginBottom: 8,
  },
};
